// src/components/tasks/DashboardTaskWidget.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { taskService } from '../../services/task.service';
import { Task, TaskStats } from '../../types/task.types';
import { formatSafeDate } from '../../utils/dateUtils';

export const DashboardTaskWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const { selectedProjectId } = useProject();
  const [urgentTasks, setUrgentTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedProjectId]);

  const loadTasks = async () => {
    if (!currentUser) return;

    try {
      // Scope to the active project so other projects' tasks don't leak in.
      const projectId = selectedProjectId || undefined;
      const [tasks, taskStats] = await Promise.all([
        taskService.getUrgentTasks(currentUser.uid, 5, projectId),
        taskService.getTaskStats({ userId: currentUser.uid, projectId })
      ]);

      setUrgentTasks(tasks);
      setStats(taskStats);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await taskService.updateTaskStatus(taskId, 'completed');
      loadTasks();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case 'lead_followup': return 'bg-secondary-100 text-secondary-600';
      case 'document_upload': return 'bg-coral-100 text-coral-500';
      case 'training': return 'bg-accent-100 text-accent-600';
      case 'contract_signature': return 'bg-purple-100 text-purple-600';
      case 'consultation_request': return 'bg-blue-100 text-blue-600';
      default: return 'bg-primary-100 text-primary-600';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-white">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">My Tasks</h3>
          <Link
            to="/tasks"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="flex gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-secondary-500" />
              <span className="text-sm text-gray-600">{stats.dueToday} due today</span>
            </div>
            {stats.overdue > 0 && (
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-600 font-medium">{stats.overdue} overdue</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-accent-500" />
              <span className="text-sm text-gray-600">{stats.completed} completed</span>
            </div>
          </div>
        )}
      </div>

      {/* Task List */}
      <div className="divide-y divide-gray-50">
        {urgentTasks.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle className="h-10 w-10 text-accent-400 mx-auto mb-2" />
            <p className="text-gray-500">All caught up! No urgent tasks.</p>
          </div>
        ) : (
          urgentTasks.map((task) => {
            const isOverdue = (task.dueDate?.toDate?.() || new Date()) < new Date() && task.status !== 'completed';

            return (
              <div
                key={task.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    className="mt-0.5 w-5 h-5 rounded-full border-2 border-gray-300 hover:border-accent-500 hover:bg-accent-50 transition-colors flex-shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-800 truncate">{task.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>

                    {task.description && (
                      <p className="text-sm text-gray-500 truncate mb-1">{task.description}</p>
                    )}

                    <div className="flex items-center gap-3 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${getTaskTypeIcon(task.type)}`}>
                        {task.type.replace('_', ' ')}
                      </span>
                      <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        <Clock className="h-3 w-3" />
                        {isOverdue ? 'Overdue: ' : 'Due: '}
                        {formatSafeDate(task.dueDate, 'MMM d, h:mm a')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DashboardTaskWidget;
